import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import ListIcon from '@mui/icons-material/List';
import ViewListIcon from '@mui/icons-material/ViewList';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import {
  CampaignHistoryRow,
  ScheduleHistoryRow,
  ScheduleStatus,
  MailingType,
  WebinarClient,
} from '../../helpers/webinarMailingData';
import moment from 'moment';
import { germanNow } from '../../helpers/germanTime';

type ViewMode = 'campaigns' | 'schedules';

const HistoryTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('schedules');
  const [campaignData, setCampaignData] = useState<CampaignHistoryRow[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<WebinarClient[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ScheduleStatus[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<MailingType | ''>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [scheduleToCancel, setScheduleToCancel] = useState<ScheduleHistoryRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Retry dialog
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [scheduleToRetry, setScheduleToRetry] = useState<ScheduleHistoryRow | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryDatetime, setRetryDatetime] = useState<string>('');
  const [needsDatetimePicker, setNeedsDatetimePicker] = useState(false);

  // Load clients
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await fetchData<WebinarClient[]>('/api/webinar-mailings/clients');
        if (response.success && response.data) {
          setClients(response.data);
        }
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    };
    loadClients();
  }, []);

  // Load history data
  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('view', viewMode);

      statusFilter.forEach((s) => params.append('status', s));
      clientFilter.forEach((c) => params.append('client_code', c));
      if (typeFilter) params.set('mailing_type', typeFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await fetchData<CampaignHistoryRow[] | ScheduleHistoryRow[]>(
        `/api/webinar-mailings/history?${params.toString()}`,
      );

      if (response.success && response.data) {
        if (viewMode === 'campaigns') {
          setCampaignData(response.data as CampaignHistoryRow[]);
        } else {
          setScheduleData(response.data as ScheduleHistoryRow[]);
        }
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [viewMode, statusFilter, clientFilter, typeFilter, startDate, endDate]);

  const handleCancelClick = (schedule: ScheduleHistoryRow) => {
    setScheduleToCancel(schedule);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!scheduleToCancel) return;

    setCancelling(true);
    try {
      const response = await fetchData(
        `/api/webinar-mailings/schedules/${scheduleToCancel.schedule_id}`,
        {
          method: 'DELETE',
        },
      );

      if (response.success) {
        setCancelDialogOpen(false);
        setScheduleToCancel(null);
        loadHistory();
      } else {
        setError('Fehler beim Abbrechen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      setError('Fehler beim Abbrechen');
      console.error(err);
    } finally {
      setCancelling(false);
    }
  };

  // Retry handlers
  const handleRetryClick = (row: ScheduleHistoryRow) => {
    setScheduleToRetry(row);

    // Check if original datetime is in the past.
    // scheduled_datetime is a Berlin-wallclock DB value that arrives UTC-tagged, so it must be
    // read with moment.utc() and compared against germanNow() — the same coordinate system.
    // A plain moment(dbValue) reads it as +1/+2h and a plain moment() is real local time, so the
    // old comparison declared a just-passed send to still be in the future: the picker never
    // appeared and the retry silently reused a past datetime for 2h after every send.
    const originalDatetime = moment.utc(row.scheduled_datetime);
    const isPast = originalDatetime.isBefore(germanNow());

    setNeedsDatetimePicker(isPast);
    if (isPast) {
      // Default to now + 15 minutes, rounded to 15-min mark. germanNow() keeps the datetime-local
      // value in Berlin wallclock — what the operator reads and what the server stores.
      const defaultNew = germanNow().add(15, 'minutes');
      const roundedMinutes = Math.ceil(defaultNew.minute() / 15) * 15;
      defaultNew.minute(roundedMinutes % 60).second(0);
      if (roundedMinutes >= 60) defaultNew.add(1, 'hour');
      setRetryDatetime(defaultNew.format('YYYY-MM-DDTHH:mm'));
    } else {
      setRetryDatetime(originalDatetime.format('YYYY-MM-DDTHH:mm'));
    }

    setRetryDialogOpen(true);
  };

  const handleRetryConfirm = async () => {
    if (!scheduleToRetry) return;
    setRetrying(true);
    try {
      const response = await fetchData(
        `/api/webinar-mailings/schedules/${scheduleToRetry.schedule_id}/retry`,
        {
          method: 'POST',
          body: JSON.stringify({
            scheduled_datetime: needsDatetimePicker ? retryDatetime : undefined,
          }),
        },
      );
      if (response.success) {
        loadHistory();
        setRetryDialogOpen(false);
        setScheduleToRetry(null);
      } else {
        setError(response.error || 'Fehler beim erneuten Planen');
      }
    } catch (err) {
      setError('Fehler beim erneuten Planen');
      console.error(err);
    } finally {
      setRetrying(false);
    }
  };

  const getScheduleStatusChip = (status: ScheduleStatus) => {
    const config: Record<
      ScheduleStatus,
      { color: 'default' | 'warning' | 'success' | 'error'; label: string }
    > = {
      pending: { color: 'default', label: 'Ausstehend' },
      creating: { color: 'default', label: 'Wird angelegt' },
      // DNV heat-up waiting for the morning worker (2026-09-10)
      awaiting_suppression: { color: 'default', label: 'Wartet (Morning-Worker)' },
      scheduled: { color: 'warning', label: 'Geplant' },
      sent: { color: 'success', label: 'Gesendet' },
      failed: { color: 'error', label: 'Fehlgeschlagen' },
      cancelled: { color: 'default', label: 'Abgebrochen' },
    };
    const { color, label } = config[status];
    return (
      <Chip
        size="small"
        label={label}
        color={color}
        sx={status === 'cancelled' ? { textDecoration: 'line-through' } : {}}
      />
    );
  };

  const getTypeChip = (type: MailingType) => (
    <Chip
      size="small"
      label={type === 'heatup' ? 'Heat-Up' : 'Redlink'}
      sx={{
        backgroundColor: type === 'heatup' ? '#ff9800' : '#2196f3',
        color: 'white',
      }}
    />
  );

  const getClientName = (code: string) => {
    const client = clients.find((c) => c.code === code);
    return client?.name || code.toUpperCase();
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<ScheduleStatus[]>) => {
    const value = event.target.value;
    setStatusFilter(typeof value === 'string' ? (value.split(',') as ScheduleStatus[]) : value);
  };

  const handleClientFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setClientFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const renderScheduleStatsChips = (stats: CampaignHistoryRow['schedule_stats']) => {
    const items = [
      { count: stats.sent, color: '#4caf50', label: 'Gesendet' },
      { count: stats.scheduled, color: '#ff9800', label: 'Geplant' },
      { count: stats.pending, color: '#9e9e9e', label: 'Ausstehend' },
      { count: stats.failed, color: '#f44336', label: 'Fehlgeschlagen' },
      { count: stats.cancelled, color: '#757575', label: 'Abgebrochen' },
    ].filter((i) => i.count > 0);

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {items.map((item, idx) => (
          <Tooltip key={idx} title={item.label}>
            <Chip
              size="small"
              label={item.count}
              sx={{ backgroundColor: item.color, color: 'white', minWidth: 28 }}
            />
          </Tooltip>
        ))}
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* View Toggle and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="schedules">
              <ViewListIcon sx={{ mr: 1 }} /> Zeitplan-Details
            </ToggleButton>
            <ToggleButton value="campaigns">
              <ListIcon sx={{ mr: 1 }} /> Kampagnen-Übersicht
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
              renderValue={(selected) => (selected.length > 0 ? `${selected.length} gewählt` : '')}
            >
              <MenuItem value="pending">Ausstehend</MenuItem>
              <MenuItem value="awaiting_suppression">Wartet (Morning-Worker)</MenuItem>
              <MenuItem value="scheduled">Geplant</MenuItem>
              <MenuItem value="sent">Gesendet</MenuItem>
              <MenuItem value="failed">Fehlgeschlagen</MenuItem>
              <MenuItem value="cancelled">Abgebrochen</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Client</InputLabel>
            <Select
              multiple
              value={clientFilter}
              onChange={handleClientFilterChange}
              label="Client"
              renderValue={(selected) => (selected.length > 0 ? `${selected.length} gewählt` : '')}
            >
              {clients.map((client) => (
                <MenuItem key={client.code} value={client.code}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as MailingType | '')}
              label="Typ"
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="heatup">Heat-Up</MenuItem>
              <MenuItem value="redlink">Redlink</MenuItem>
            </Select>
          </FormControl>

          <TextField
            type="date"
            label="Von"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <TextField
            type="date"
            label="Bis"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          {(statusFilter.length > 0 ||
            clientFilter.length > 0 ||
            typeFilter ||
            startDate ||
            endDate) && (
            <Button
              size="small"
              onClick={() => {
                setStatusFilter([]);
                setClientFilter([]);
                setTypeFilter('');
                setStartDate('');
                setEndDate('');
              }}
            >
              Filter zurücksetzen
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'schedules' ? (
        /* Schedule Detail View */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                <TableCell>Kampagne</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Geplant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gesendet</TableCell>
                <TableCell align="right">Empfänger</TableCell>
                <TableCell align="right">Öffnungen</TableCell>
                <TableCell align="right">Öffnungs-%</TableCell>
                <TableCell align="right">Klicks</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">Abmeld.</TableCell>
                <TableCell align="right">Registr.</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduleData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography color="text.secondary">Keine Daten gefunden</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                scheduleData.map((row) => (
                  <TableRow
                    key={row.is_non_opener ? `no-${row.schedule_id}` : row.schedule_id}
                    hover
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.campaign_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTypeChip(row.mailing_type)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">#{row.mailing_number}</Typography>
                        {row.is_non_opener && (
                          <Chip
                            size="small"
                            label="Non-Opener"
                            color="info"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.email_subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={getClientName(row.client_code)}
                          variant="outlined"
                        />
                        {row.arm && row.arm !== '-' && (
                          <Chip
                            size="small"
                            label={`Betreff ${row.arm}`}
                            color={row.arm === 'A' ? 'primary' : 'secondary'}
                            title="Betreff-A/B: eine Hälfte der Welle (Arm), Betreff = gesendeter Betreff"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.esp_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {moment.utc(row.scheduled_datetime).format('DD.MM.YYYY HH:mm')}
                    </TableCell>
                    <TableCell>
                      {getScheduleStatusChip(row.status)}
                      {row.error_message && (
                        <Tooltip title={row.error_message}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              display: 'block',
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.error_message}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.sent_at ? moment.utc(row.sent_at).format('DD.MM.YYYY HH:mm') : '-'}
                    </TableCell>
                    {/* ESP Metrics */}
                    {row.metrics ? (
                      <>
                        <TableCell align="right">
                          {formatNumber(row.metrics.recipients, 0)}
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.metrics.opens, 0)}</TableCell>
                        <TableCell align="right">
                          {(row.metrics.openRate * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.metrics.clicks, 0)}</TableCell>
                        <TableCell align="right">{(row.metrics.ctr * 100).toFixed(1)}%</TableCell>
                        <TableCell align="right">
                          {formatNumber(row.metrics.unsubscribes, 0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(row.metrics.registrations, 0)}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right" colSpan={7}>
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {row.can_cancel && (
                        <Tooltip title="Zeitplan abbrechen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelClick(row)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.status === 'failed' && !row.is_non_opener && (
                        <Tooltip title="Erneut planen">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleRetryClick(row)}
                          >
                            <ReplayIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Campaign Overview */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                <TableCell>ID</TableCell>
                <TableCell>Kampagne</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Webinar</TableCell>
                <TableCell>E-Mails</TableCell>
                <TableCell>Versand-Status</TableCell>
                <TableCell>Erstellt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaignData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">Keine Daten gefunden</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                campaignData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{getTypeChip(row.mailing_type)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.webinar_title || '-'}
                      </Typography>
                      {row.webinar_date && (
                        <Typography variant="caption" color="text.secondary">
                          {moment.utc(row.webinar_date).format('DD.MM.YYYY HH:mm')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.email_count}</TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                      >
                        {renderScheduleStatsChips(row.schedule_stats)}
                        {(row.non_opener_sent_count || 0) > 0 && (
                          <Chip
                            size="small"
                            label={`${row.non_opener_sent_count} Non-Opener`}
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{moment(row.created_at).format('DD.MM.YYYY')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Zeitplan abbrechen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {scheduleToCancel && (
              <>
                Möchten Sie den Zeitplan für{' '}
                <strong>E-Mail #{scheduleToCancel.mailing_number}</strong> an{' '}
                <strong>{getClientName(scheduleToCancel.client_code)}</strong> wirklich abbrechen?
                <br />
                <br />
                Geplanter Versand:{' '}
                {moment.utc(scheduleToCancel.scheduled_datetime).format('DD.MM.YYYY HH:mm')}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Nein, behalten
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={20} /> : 'Ja, abbrechen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Confirmation Dialog */}
      <Dialog open={retryDialogOpen} onClose={() => setRetryDialogOpen(false)}>
        <DialogTitle>Erneut planen?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {scheduleToRetry && (
              <>
                Möchten Sie <strong>E-Mail #{scheduleToRetry.mailing_number}</strong> an{' '}
                <strong>{getClientName(scheduleToRetry.client_code)}</strong> erneut planen?
                <br />
                <br />
                <Typography variant="caption" color="error">
                  Ursprünglicher Fehler: {scheduleToRetry.error_message}
                </Typography>
                {scheduleToRetry.esp_type === 'iterable' && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Vorher in Iterable prüfen, ob die Kampagne trotz Fehler angelegt wurde (Timeout
                    nach serverseitigem Erfolg) — sonst geht sie doppelt raus.
                    {scheduleToRetry.arm && scheduleToRetry.arm !== '-'
                      ? ` Diese Zeile ist Arm ${scheduleToRetry.arm} eines Betreff-A/B-Paars; der andere Arm bleibt unberührt.`
                      : ''}
                  </Typography>
                )}
              </>
            )}
          </DialogContentText>

          {needsDatetimePicker ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Der ursprüngliche Zeitpunkt liegt in der Vergangenheit. Bitte wählen Sie einen
                neuen:
              </Typography>
              <TextField
                type="datetime-local"
                fullWidth
                value={retryDatetime}
                onChange={(e) => setRetryDatetime(e.target.value)}
                inputProps={{ min: moment().format('YYYY-MM-DDTHH:mm') }}
              />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Geplanter Zeitpunkt:{' '}
              <strong>
                {scheduleToRetry &&
                  moment.utc(scheduleToRetry.scheduled_datetime).format('DD.MM.YYYY HH:mm')}
              </strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryDialogOpen(false)} disabled={retrying}>
            Abbrechen
          </Button>
          <Button
            onClick={handleRetryConfirm}
            color="primary"
            variant="contained"
            disabled={retrying}
          >
            {retrying ? <CircularProgress size={20} /> : 'Ja, erneut planen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryTab;
